import z from "zod"

import { CountryIso3166Schema, LanguageIso6391 } from "@tfg/types/schemas/enum"
import { standardEmail } from "@tfg/types/schemas/utils"

import {
  createContactBusinessRelationshipRequestDtoTypeEnum,
  registrationIdRequestDtoTypeEnum,
} from "~/gen/api/types"
import { coordinatesSchema } from "~/pages/Admin/components/Fields/coordinatesSchema"
import {
  errorMap,
  StringNoSpecialCharactersSchema,
} from "~/pages/SupplierPortal/TraceabilityRequests/components/ZodCustomErrorMap.tsx"

const registrationIdSchema = z.object({
  id: z.string().optional(),
  registrationId: z.string({ error: errorMap }),
  type: z.enum(registrationIdRequestDtoTypeEnum),
})

const businessRelationshipSchema = z.object({
  isActive: z.boolean().default(true),
  supplierCode: z.string().nullable(),
  type: z.enum(createContactBusinessRelationshipRequestDtoTypeEnum),
  tagNames: z.array(z.string().max(16)).optional(),
})

export const formSchema = z
  .object({
    supplierId: z.string().optional(),
    businessRelationshipId: z.string().optional(),
    canAnswerOnBehalf: z.boolean().optional(),
    name: z.string().optional(),
    email: z.string().optional(),
    preferedLangageIso6391: z.enum(LanguageIso6391).optional(),
    organization: z.object({
      businessRelationship: businessRelationshipSchema,
      name: z.string({ error: errorMap }).min(2).max(100),
      street: z.string({ error: errorMap }).min(2).max(200),
      city: z.string().min(2).max(200),
      postalCode: z.string({ error: errorMap }).min(2).max(30),
      country: z.enum(CountryIso3166Schema.enum),
      registrationId: z.array(registrationIdSchema).default([]),
      coordinates: coordinatesSchema.nullable(),
    }),
  })
  .superRefine((data, ctx) => {
    const canAnswerOnBehalf = data.canAnswerOnBehalf === true
    const hasContactName =
      data.name !== undefined && data.name.trim().length > 0
    const hasContactEmail =
      data.email !== undefined && data.email.trim().length > 0
    const skipContactValidation =
      canAnswerOnBehalf && !hasContactName && !hasContactEmail

    if (skipContactValidation) {
      return
    }

    if (!hasContactName) {
      ctx.addIssue({
        code: "custom",
        message: "Contact name is required",
        path: ["name"],
      })
    } else {
      const nameResult = StringNoSpecialCharactersSchema.safeParse(data.name)
      if (!nameResult.success) {
        for (const issue of nameResult.error.issues) {
          ctx.addIssue({ ...issue, path: ["name"] })
        }
      }
    }

    if (!hasContactEmail) {
      ctx.addIssue({
        code: "custom",
        message: "Contact email is required",
        path: ["email"],
      })
    } else {
      const emailResult = standardEmail().safeParse(data.email)
      if (!emailResult.success) {
        for (const issue of emailResult.error.issues) {
          ctx.addIssue({ ...issue, path: ["email"] })
        }
      }
    }

    if (!data.preferedLangageIso6391) {
      ctx.addIssue({
        code: "custom",
        message: "Preferred language is required",
        path: ["preferedLangageIso6391"],
      })
    }
  })

export type ContactFormType = z.infer<typeof formSchema>
